import type { Parent, PhrasingContent, Root } from 'mdast';
import type { CitationAttrs, CitationMdastNode } from './types';

const SELF_CLOSING_RX = /^<citation((?:\s+\w+="[^"]*")+)\s*\/>$/i;
const OPEN_RX = /^<citation((?:\s+\w+="[^"]*")+)\s*>$/i;
const CLOSE_RX = /^<\/citation\s*>$/i;

function parseAttrs(attrStr: string): CitationAttrs {
	const attrs: CitationAttrs = {};
	const chunkMatch = attrStr.match(/\bchunk_id="(\d+)"/i);
	const docMatch = attrStr.match(/\bdoc_id="(\d+)"/i);
	const webMatch = attrStr.match(/\bweb_id="([^"]+)"/i);
	const hashMatch = attrStr.match(/\bhash="([^"]+)"/i);
	if (chunkMatch) {
		attrs.chunkId = parseInt(chunkMatch[1], 10);
	}
	if (docMatch) {
		attrs.docId = parseInt(docMatch[1], 10);
	}
	if (webMatch) {
		attrs.webId = webMatch[1];
	}
	if (hashMatch) {
		attrs.hash = hashMatch[1];
	}
	return attrs;
}

function makeCitationNode(attrs: CitationAttrs): CitationMdastNode {
	return { type: 'citation', attrs };
}

function processChildren(children: PhrasingContent[]): PhrasingContent[] {
	const result: PhrasingContent[] = [];
	let i = 0;

	while (i < children.length) {
		const child = children[i];

		if (child.type !== 'html') {
			result.push(child);
			i++;
			continue;
		}

		const selfMatch = child.value.match(SELF_CLOSING_RX);
		if (selfMatch) {
			result.push(makeCitationNode(parseAttrs(selfMatch[1])));
			i++;
			continue;
		}

		const openMatch = child.value.match(OPEN_RX);
		if (openMatch) {
			const attrs = parseAttrs(openMatch[1]);
			const quoteParts: string[] = [];
			let j = i + 1;
			let found = false;

			while (j < children.length) {
				const inner = children[j];
				if (inner.type === 'html' && CLOSE_RX.test(inner.value)) {
					attrs.quote = quoteParts.join('').trim() || undefined;
					result.push(makeCitationNode(attrs));
					i = j + 1;
					found = true;
					break;
				}
				if (inner.type === 'text') {
					quoteParts.push(inner.value);
				}
				j++;
			}

			if (!found) {
				// No matching close tag — leave as-is (streaming/incomplete)
				result.push(child);
				i++;
			}
			continue;
		}

		result.push(child);
		i++;
	}

	return result;
}

function visitParent(node: Parent): void {
	// Recurse into block-level children first
	for (const child of node.children) {
		if ('children' in child) {
			visitParent(child as Parent);
		}
	}

	// Process phrasing content children for citation patterns
	const anyChildren = node.children as PhrasingContent[];
	const hasHtml = anyChildren.some((c) => c.type === 'html');
	if (!hasHtml) {
		return;
	}
	(node as any).children = processChildren(anyChildren);
}

export function remarkCitations() {
	return (tree: Root) => {
		visitParent(tree as unknown as Parent);
	};
}
